/* eslint-disable no-console -- used for adapter init logging */
/**
 * Instrumentation orchestrator.
 *
 * All telemetry calls in the app go through this module. Spans are handled
 * directly via the Performance API (spans.js) so they always appear in
 * DevTools regardless of backend. The adapter handles backend-specific
 * concerns only (NR interactions, page actions, user attributes).
 *
 *   NEWRELIC_APPLICATION_ID → NR adapter initialized (staging/production)
 *   (not set)              → no-op adapter; marks/measures still fire
 *
 * Navigation lifecycle:
 *   startNavigation(name) begins tracking. The navigation is considered
 *   "settled" when either:
 *     1. endNavigation() is called explicitly by a component, OR
 *     2. settleNavigation() is called by the telemetry plugin when all
 *        tracked async actions complete
 *   Whichever fires first wins — the second call is a no-op.
 */

import { NEWRELIC_APPLICATION_ID } from "@/common/Env";

import nrAdapter from "./adapters/newrelic";
import { startSpan, endSpan as _endSpan, withSpan as _withSpan } from "./spans";

const sessionId =
  typeof crypto !== "undefined" && crypto.randomUUID
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2)}`;

let _navStartMark = null;
let _navRoute = null;

const noop = () => {};
let _adapter = { setUser: noop, startNavigation: noop, endNavigation: noop, addEvent: noop };

function initAdapter() {
  if (NEWRELIC_APPLICATION_ID) {
    try {
      nrAdapter.init();
      _adapter = nrAdapter;
      console.debug("[instrumentation] adapter: newrelic");
    } catch (e) {
      console.warn("[instrumentation] NR init failed, marks/measures still active", e);
    }
  } else {
    console.debug("[instrumentation] no backend configured, marks/measures only");
  }
}

initAdapter();

const setUser = (user, team) => {
  _adapter.setUser(user, team);
};

const startNavigation = (name) => {
  _navStartMark = `nav:${name}:${performance.now()}`;
  _navRoute = name;
  performance.mark(_navStartMark);
  _adapter.startNavigation(name);
};

/**
 * End navigation explicitly (called by components when they consider the page "ready").
 */
const endNavigation = () => {
  _settleNavigation("explicit");
};

/**
 * Settle navigation automatically (called by the telemetry plugin when all
 * tracked actions complete after a navigation started).
 */
const settleNavigation = () => {
  _settleNavigation("auto");
};

function _settleNavigation(trigger) {
  if (!_navStartMark) return;

  try {
    const entry = performance.measure(`navigation:settled [${trigger}]`, _navStartMark);
    const durationMs = Math.round(entry.duration);
    _adapter.addEvent("navigation:settled", {
      "route.name": _navRoute,
      durationMs,
      trigger,
    });
  } catch (_) {
    // mark may have been cleared
  }

  _navStartMark = null;
  _navRoute = null;
  _adapter.endNavigation();
}

/**
 * Returns the current navigation state for the telemetry plugin.
 * Returns null if no navigation is in progress.
 */
const getNavigationState = () => {
  if (!_navStartMark) return null;
  return { startMark: _navStartMark, route: _navRoute };
};

// A failed span is still named "<name> [error]" as a performance measure, but the
// error itself is not reported anywhere remotely — we don't send errors to New Relic.
const endSpan = (span, error) => {
  _endSpan(span, error);
};

const withSpan = (name, fn, attrs = {}) => _withSpan(name, fn, attrs);

const addEvent = (name, attrs = {}) => {
  _adapter.addEvent(name, attrs);
};

export default { setUser, startNavigation, endNavigation, settleNavigation, startSpan, endSpan, addEvent, withSpan };
export { sessionId, getNavigationState };
